import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import Label from "../Label";
import FeatureUpdateArticle from "./FeatureUpdateArticle";
import type { FeatureUpdate } from "../../static/updates/featureUpdates";

type FeatureUpdatePopupDialogProps = {
  update: FeatureUpdate;
  open: boolean;
  /** Closing in any way, or following a link in the update, is "seen". */
  onClose: () => void;
  /** "לכל העדכונים": the full updates window. */
  onOpenAll?: () => void;
};

/** A big update, opened by itself: the update as the updates window shows it, in a focused dialog. */
export default function FeatureUpdatePopupDialog({ update, open, onClose, onOpenAll }: FeatureUpdatePopupDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      aria-labelledby="feature-update-popup-title"
      sx={{ "& .MuiDialog-paper": { maxWidth: 680 } }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Label color="success">{t("featureUpdates.popup.badge")}</Label>
          {/* The dialog's own h2, named by the Dialog's aria-labelledby. */}
          <DialogTitle sx={{ p: 0, mt: 1, typography: "h5" }}>{update.title}</DialogTitle>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t("featureUpdates.popup.close")}>
          <Close />
        </IconButton>
      </Box>
      <DialogContent
        dividers
        // A link in the update (its call to action) leaves the popup: that counts as seen.
        onClickCapture={(event) => {
          if ((event.target as HTMLElement).closest("a")) onClose();
        }}
      >
        <FeatureUpdateArticle update={update} contentOnly />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {onOpenAll && (
          <Button color="inherit" onClick={onOpenAll}>
            {t("featureUpdates.popup.allUpdates")}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={onClose}>
          {t("featureUpdates.popup.gotIt")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
